import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowRight, CircleCheck, LoaderCircle } from 'lucide-react'
import { sendJson } from '@/lib/request'
import { Button } from '@/components/generals/button'

import {
    Field,
    FormMessage,
    SelectInput,
    TextArea,
    TextInput
} from '@/components/generals/form-field'

import { buyerProfiles, viewingTimes } from '@/constants/estate'

type FormValues = {
    full_name: string
    email: string
    phone: string
    buyer_profile: string
    preferred_time: string
    message: string
    company: string
}

const emptyValues: FormValues = {
    full_name: '',
    email: '',
    phone: '',
    buyer_profile: '',
    preferred_time: '',
    message: '',
    company: ''
}

const validate = (values: FormValues): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (values.full_name.trim().length < 2) errors.full_name = "Please tell us your name."
    if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) errors.email = "Please enter a valid email address."
    if (values.phone.trim() && !/^\+?[\d\s()-]{7,20}$/.test(values.phone.trim())) errors.phone = "Please enter a valid phone number."

    return errors
}

/*
   Private viewing enquiry form
   Two-column grid on larger screens, single column on mobile. Validates in the
   browser first, then saves through /api/enquiries, which also emails the
   estate. A hidden honeypot field quietly filters out bots.
*/
export const ViewingForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(emptyValues)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
    const [message, setMessage] = useState('')
    const [submittedName, setSubmittedName] = useState('')

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setValues(current => ({ ...current, [field]: event.target.value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: '' }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            setStatus('error')
            setMessage("Please check the highlighted fields.")
            return
        }

        setStatus('submitting')
        setMessage('')

        const result = await sendJson('/api/enquiries', 'POST', values)

        if (result.ok) {
            setSubmittedName(values.full_name.trim().split(' ')[0])
            setValues(emptyValues)
            setStatus('success')
            return
        }

        setErrors(result.data.errors ?? {})
        setStatus('error')
        setMessage(result.data.message ?? "Something went wrong. Please try again.")
    }

    if (status === 'success') {
        return (
            <div className='flex h-full flex-col items-start justify-center rounded-3xl border border-stone bg-cream p-8 sm:p-12' role='status'>
                <span className='inline-flex size-14 items-center justify-center rounded-full bg-forest-soft text-forest animate-in zoom-in-75 fade-in duration-500'>
                    <CircleCheck aria-hidden='true' className='size-6' />
                </span>

                <h3 className='mt-8 font-heading text-3xl leading-tight'>
                    Thank you{submittedName ? `, ${submittedName}` : ''}.
                </h3>

                <p className='mt-4 max-w-md leading-relaxed text-muted-foreground'>
                    Your request is with us. We'll be in touch shortly to arrange a private viewing at a time that suits you.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='link-underline mt-8 pb-0.5 text-sm font-medium text-forest'
                >
                    Send another request
                </button>
            </div>
        )
    }

    const invalid = (field: keyof FormValues) => Boolean(errors[field])
    const describedBy = (field: keyof FormValues) => errors[field] ? `${field}-error` : undefined

    return (
        <form
            noValidate
            onSubmit={onSubmit}
            aria-label='Request a private viewing'
            className='relative rounded-3xl border border-stone bg-cream p-6 shadow-[0_40px_80px_-50px_rgba(34,34,34,0.35)] sm:p-10'
        >
            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id='full_name' label='Full name' error={errors.full_name}>
                    <TextInput
                        id='full_name'
                        name='full_name'
                        autoComplete='name'
                        value={values.full_name}
                        onChange={update('full_name')}
                        aria-invalid={invalid('full_name')}
                        aria-describedby={describedBy('full_name')}
                        required
                    />
                </Field>

                <Field id='email' label='Email' error={errors.email}>
                    <TextInput
                        id='email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={invalid('email')}
                        aria-describedby={describedBy('email')}
                        required
                    />
                </Field>

                <Field id='phone' label='Phone' optional error={errors.phone}>
                    <TextInput
                        id='phone'
                        name='phone'
                        type='tel'
                        autoComplete='tel'
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={invalid('phone')}
                        aria-describedby={describedBy('phone')}
                    />
                </Field>

                <Field id='buyer_profile' label="I'm" optional error={errors.buyer_profile}>
                    <SelectInput
                        id='buyer_profile'
                        name='buyer_profile'
                        value={values.buyer_profile}
                        onChange={update('buyer_profile')}
                        aria-invalid={invalid('buyer_profile')}
                    >
                        <option value=''>Choose one</option>

                        {buyerProfiles.map(profile => (
                            <option key={profile} value={profile}>
                                {profile}
                            </option>
                        ))}
                    </SelectInput>
                </Field>

                <Field id='preferred_time' label='Best time to view' optional className='sm:col-span-2' error={errors.preferred_time}>
                    <SelectInput
                        id='preferred_time'
                        name='preferred_time'
                        value={values.preferred_time}
                        onChange={update('preferred_time')}
                        aria-invalid={invalid('preferred_time')}
                    >
                        <option value=''>Choose a time</option>

                        {viewingTimes.map(time => (
                            <option key={time} value={time}>
                                {time}
                            </option>
                        ))}
                    </SelectInput>
                </Field>

                <Field id='message' label='Anything we should know?' optional className='sm:col-span-2'>
                    <TextArea
                        id='message'
                        name='message'
                        rows={4}
                        value={values.message}
                        onChange={update('message')}
                        placeholder="Timing, questions, or what matters most to you in a new home."
                    />
                </Field>
            </div>

            {/* Honeypot: hidden from people and screen readers, tempting to bots */}
            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='company'>Company</label>
                <input id='company' name='company' tabIndex={-1} autoComplete='off' value={values.company} onChange={update('company')} />
            </div>

            {status === 'error' && message && (
                <FormMessage tone='error' className='mt-6'>
                    {message}
                </FormMessage>
            )}

            <div className='mt-8 flex flex-col gap-5 border-t border-stone pt-6 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-xs leading-relaxed text-muted-foreground'>
                    We'll only use your details to arrange your viewing and answer your questions.
                </p>

                <Button type='submit' size='lg' disabled={status === 'submitting'} className='shrink-0'>
                    {status === 'submitting'
                        ? <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                        : null}

                    {status === 'submitting' ? 'Sending' : 'Request a viewing'}

                    {status !== 'submitting' && (
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:translate-x-0.5' />
                    )}
                </Button>
            </div>
        </form>
    )
}
